import * as THREE from 'three';
import type { Target } from '@/ui/attention';

/**
 * The ring that says *this one*.
 *
 * Two marks, and the difference between them is the difference between looking
 * and meaning: a broken one turns slowly under the pointer over whatever it
 * crosses, and a second, closed one stays on the thing the god has actually
 * chosen. Both sit at the thing's base and lie ON the world, because a
 * screen-space box would be chrome and this is supposed to be attention
 * happening *in* the landscape.
 *
 * ## The pointer's ring is quiet, and measured
 *
 * The first version was a solid white annulus at 0.72 opacity and it read as a
 * game cursor: something the interface had put on the world. `tools/chrome.ts`
 * puts the ring inks through the same tone map and the same sRGB framebuffer
 * blend the renderer uses and says why — the halo at 0.35 delivered 1.71:1
 * against median ground and the hover ring at 0.72 delivered 3.08:1, nearly
 * twice it. So the halo's own number is taken as this project's definition of
 * *quiet*, and the pointer ring is priced against it: at 0.36 it delivers
 * 1.8:1, as quiet as the mark the design was already happy with. It is
 * **broken**, not closed — a closed ring is a selection box; three arcs with
 * air between them is a mark someone made — and it turns once in forty
 * seconds, two orders quieter than a spinner.
 *
 * ## The chosen mark is a circle drawn on the ground (2026-08-17)
 *
 * The chosen mark used to be one step above the hover ring and no more: gold,
 * closed, four ticks, a breath. The art direction wants a *drawn circle* —
 * gold, lit from within, a ring of small marks turning the other way, a soft
 * light on the ground under it, a few motes rising off it — and it gets one.
 * It is louder than the measured quiet on purpose: this is the god's own
 * attention made visible in the world, the one mark on screen that is meant
 * to read as divine rather than as chrome, and it is the thing the post
 * pass's bloom is allowed to catch. It is still no colour but gold, still
 * breathes on an eleven-second period, and it draws additively so it lights
 * the ground rather than painting over it. `tools/chrome.ts`'s hover-ring
 * numbers are untouched.
 *
 * ## …and it is DRAWN ON THE GROUND, and things stand in front of it (2026-08-19)
 *
 * Griffin's direction: the mark should have depth — go behind the things
 * between it and the eye instead of being printed over them — and it should
 * follow the ground's elevation instead of floating over a slope on a flat
 * plane. Both are one change of model. Every ring is now a STRIP DRAPED OVER
 * THE TERRAIN: its vertices are sampled from `groundAt` around the circle,
 * so on a hillside the uphill half of the ring is higher than the downhill
 * half, exactly as a circle scratched into the turf would be; and the marks
 * draw with the depth test ON, lifted a hand off the turf and pulled toward
 * the eye by a polygon offset, so the ground never fights them and a trunk,
 * a boulder or a ridge between the eye and the far side of the ring hides
 * that side, as it would hide chalk on the grass. A mark you cannot see
 * because a hill is in the way is a mark behind a hill, which is where the
 * thing you chose is too.
 *
 * The cost is sampling the ground. It is cached: a ring around a tree is
 * sampled once and never again until the target moves two per cent of the
 * ring's radius or the ring changes size; a ring around a walking animal is
 * re-sampled as it walks, at 24 angles when the ring is small (the ground
 * within a few metres is very nearly a plane) and 48 when it is wide. The
 * turning ticks and the breathing halo take their heights from the sampled
 * circles by interpolation, so turning and breathing cost no samples at all.
 *
 * The light under it (the underglow) stays — it is what makes the mark read
 * as something shining rather than something stencilled — and is now a
 * draped disc too, with a warmer, tighter core and a wider, fainter skirt
 * than before, so it sits IN the grass rather than on a pane over it.
 *
 * Render-only, and pointedly so — nothing in here is allowed to write
 * anything. Looking at the world must not change it.
 */

export interface Selection {
  group: THREE.Group;
  /**
   * `hover` is what the pointer is over; `chosen` is what was clicked. The
   * eye's own position, not its altitude, sets how big a ring has to be —
   * a thing three kilometres down the valley needs the same help a thing
   * underfoot does not, and altitude says nothing about either. `groundAt`
   * is what the marks are draped over.
   */
  update(tSec: number, hover: Target | null, chosen: Target | null,
         eye: { x: number; y: number; z: number },
         groundAt: (x: number, z: number) => number,
         /**
          * PLAIN marks — the mortal's (2026-08-19, Griffin: "the observe
          * selector must be much smaller and not mystical at all"). On foot the
          * chosen mark is one thin closed ring in the hover's own warm-white ink,
          * no glow, no halo, no ticks, no motes, no breath, no turn, and both
          * marks are a footprint at most a metre across rather than the thing's
          * whole size — a person's feet, a trunk's base, not a crown's shadow.
          * The divine circle is a god's attention made visible; a mortal's
          * attention is a look, and a look leaves a small mark.
          */
         plain?: boolean): void;
}

/**
 * Metres the marks clear the turf by, plus a share of the ring's radius so a
 * wide ring at range stays ahead of the depth buffer's precision there.
 */
const LIFT_BASE = 0.10, LIFT_PER_R = 0.025;
/**
 * The ring never shrinks below this on screen. A tussock of grass is 40 cm
 * across and a ring that honest is invisible from anywhere you could have
 * clicked it — so the ring is the thing's own size, or a fixed slice of the
 * distance to the eye, whichever is larger.
 */
const HOLD = 0.012;
/** The plain (mortal's) mark: half the thing's size, and never outside this band, metres. */
const PLAIN_MIN = 0.3, PLAIN_MAX = 1.0;
/** …and its ink: the hover's own warm-white, a little firmer. Not gold; gold is the god's. */
const PLAIN_ALPHA = 0.6;

/**
 * Opacities, all three measured in `tools/chrome.ts` against the island's own
 * ground distribution through the real tone map. Do not nudge these by eye:
 * the ring is a `MeshBasicMaterial`, so it is the one thing on screen whose
 * delivered contrast can be computed exactly, and it was computed.
 */
const HOVER_ALPHA = 0.36;   // 1.8:1 — the halo's own quiet, matched
/** The chosen circle: additive gold, so these are how much light it adds. */
const CHOSEN_ALPHA = 0.85;
const TICKS_ALPHA = 0.55;
const HALO_ALPHA = 0.32;
const GLOW_ALPHA = 0.42;
/** How many motes rise off a chosen mark, and how far above it they climb. */
const MOTES = 14;
const MOTE_RISE = 0.9;

/** Radians per second. One turn in 40 s; a UI spinner is one turn in one. */
const HOVER_SPIN = (2 * Math.PI) / 40;
/** The chosen mark turns the other way, slower still — 70 s to come round. */
const CHOSEN_SPIN = -(2 * Math.PI) / 70;
/** One breath in eleven seconds. Four is a pulse, and a pulse means alert. */
const BREATH = (2 * Math.PI) / 11;

/**
 * A soft radial light, drawn on a canvas once: a warm, tight gold core, a
 * wider amber skirt falling to nothing. Additive, it lights the ground the
 * circle is drawn on without ever painting over it.
 */
function glowTexture(): THREE.Texture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0.00, 'rgba(255,226,150,0.70)');
  grad.addColorStop(0.18, 'rgba(255,210,120,0.42)');
  grad.addColorStop(0.45, 'rgba(255,190,90,0.16)');
  grad.addColorStop(0.75, 'rgba(255,176,72,0.05)');
  grad.addColorStop(1.00, 'rgba(255,170,64,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A soft round mote, for the points that rise off the circle. */
function moteTexture(): THREE.Texture {
  const size = 32;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, 'rgba(255,230,160,1)');
  grad.addColorStop(0.35, 'rgba(255,205,110,0.55)');
  grad.addColorStop(1, 'rgba(255,190,90,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * The sampled circles a mark is draped over: for each of a few radii (as
 * multiples of the mark's radius), the ground height at N equal angles.
 * `heightAt(k, theta)` interpolates, so anything on circle k at any angle —
 * a turning tick, a breathing halo — gets a height without a sample.
 */
class Drape {
  readonly radii: readonly number[];
  n = 0;
  readonly h: Float32Array[];
  cx = NaN; cz = NaN; r = NaN;
  constructor(radii: readonly number[], nMax: number) {
    this.radii = radii;
    this.h = radii.map(() => new Float32Array(nMax + 1));
  }
  /** Re-sample if the centre or the radius moved enough to matter. */
  sample(cx: number, cz: number, r: number, groundAt: (x: number, z: number) => number): boolean {
    const n = r < 4 ? 24 : 48;
    const tol = r * 0.02;
    if (n === this.n && Math.abs(cx - this.cx) < tol && Math.abs(cz - this.cz) < tol
        && Math.abs(r - this.r) < tol) return false;
    this.n = n; this.cx = cx; this.cz = cz; this.r = r;
    for (let k = 0; k < this.radii.length; k++) {
      const rr = r * this.radii[k];
      const hk = this.h[k];
      for (let i = 0; i < n; i++) {
        const th = (i / n) * Math.PI * 2;
        hk[i] = groundAt(cx + Math.cos(th) * rr, cz + Math.sin(th) * rr);
      }
      hk[n] = hk[0];
    }
    return true;
  }
  heightAt(k: number, theta: number): number {
    const n = this.n;
    let u = (theta / (Math.PI * 2)) * n;
    u -= Math.floor(u / n) * n;            // wrap into [0, n)
    const i = Math.floor(u), f = u - i;
    const hk = this.h[k];
    return hk[i] * (1 - f) + hk[i + 1] * f;
  }
}

/**
 * A draped strip: `arcs` pieces of annulus at one of the drape's circles,
 * whose vertex positions are rewritten each frame from the drape. Built once
 * with the maximum segment count; `lay()` fills it.
 */
class Strip {
  readonly mesh: THREE.Mesh;
  private readonly pos: Float32Array;
  private readonly arcs: number; private readonly gap: number;
  private readonly segs: number; private readonly thick: number;
  constructor(material: THREE.Material, arcs: number, gap: number, segs: number, thick: number) {
    this.arcs = arcs; this.gap = gap; this.segs = segs; this.thick = thick;
    const verts = arcs * (segs + 1) * 2;
    this.pos = new Float32Array(verts * 3);
    const idx: number[] = [];
    for (let a = 0; a < arcs; a++) {
      const base = a * (segs + 1) * 2;
      for (let s = 0; s < segs; s++) {
        const i = base + s * 2;
        idx.push(i, i + 1, i + 3, i, i + 3, i + 2);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setIndex(idx);
    this.mesh = new THREE.Mesh(g, material);
    this.mesh.frustumCulled = false;
  }
  /**
   * Lay the strip on circle `k` of the drape at radius `r` (metres), turned
   * by `spin` radians, `lift` metres over the turf.
   */
  lay(d: Drape, k: number, r: number, spin: number, lift: number): void {
    const inner = r * (1 - this.thick);
    const span = (2 * Math.PI) / this.arcs - this.gap;
    const p = this.pos;
    let o = 0;
    for (let a = 0; a < this.arcs; a++) {
      const start = (a * 2 * Math.PI) / this.arcs + this.gap / 2 + spin;
      for (let s = 0; s <= this.segs; s++) {
        const th = start + (span * s) / this.segs;
        const c = Math.cos(th), sn = Math.sin(th);
        const y = d.heightAt(k, th) + lift;
        p[o++] = d.cx + inner * c; p[o++] = y; p[o++] = d.cz + inner * sn;
        p[o++] = d.cx + r * c;     p[o++] = y; p[o++] = d.cz + r * sn;
      }
    }
    (this.mesh.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.mesh.visible = true;
  }
}

/**
 * A draped disc for the underglow: a fan of rings from the centre out, with
 * the radial texture mapped by radius, so the glow lies in the grass of a
 * slope rather than on a pane over it.
 */
class Disc {
  readonly mesh: THREE.Mesh;
  private readonly pos: Float32Array;
  private readonly rings: readonly number[];
  private readonly segs: number;
  constructor(material: THREE.Material, rings: readonly number[], segs: number) {
    this.rings = rings; this.segs = segs;
    const verts = 1 + rings.length * segs;
    this.pos = new Float32Array(verts * 3);
    const uv = new Float32Array(verts * 2);
    uv[0] = 0.5; uv[1] = 0.5;
    for (let k = 0; k < rings.length; k++) {
      for (let s = 0; s < segs; s++) {
        const th = (s / segs) * Math.PI * 2;
        const i = 1 + k * segs + s;
        uv[i * 2] = 0.5 + Math.cos(th) * 0.5 * rings[k];
        uv[i * 2 + 1] = 0.5 + Math.sin(th) * 0.5 * rings[k];
      }
    }
    const idx: number[] = [];
    for (let s = 0; s < segs; s++) idx.push(0, 1 + (s + 1) % segs, 1 + s);
    for (let k = 0; k + 1 < rings.length; k++) {
      for (let s = 0; s < segs; s++) {
        const a = 1 + k * segs + s, b = 1 + k * segs + (s + 1) % segs;
        const c = a + segs, dd = b + segs;
        idx.push(a, dd, c, a, b, dd);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    g.setIndex(idx);
    this.mesh = new THREE.Mesh(g, material);
    this.mesh.frustumCulled = false;
  }
  /** `R` is the disc's outer radius in metres; ring k lies at R·rings[k]. */
  lay(d: Drape, firstK: number, R: number, lift: number, groundAt: (x: number, z: number) => number): void {
    const p = this.pos;
    p[0] = d.cx; p[1] = groundAt(d.cx, d.cz) + lift; p[2] = d.cz;
    let o = 3;
    for (let k = 0; k < this.rings.length; k++) {
      const rr = R * this.rings[k];
      for (let s = 0; s < this.segs; s++) {
        const th = (s / this.segs) * Math.PI * 2;
        p[o++] = d.cx + Math.cos(th) * rr;
        p[o++] = d.heightAt(firstK + k, th) + lift;
        p[o++] = d.cz + Math.sin(th) * rr;
      }
    }
    (this.mesh.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.mesh.visible = true;
  }
}

export function createSelection(): Selection {
  const group = new THREE.Group();
  group.name = 'selection';

  // Depth-tested, never depth-written, pulled a little toward the eye so the
  // turf under the strip never wins the tie, and the things standing between
  // the eye and the far side of the mark hide that side — see the header.
  const mat = (opacity: number, colour: THREE.ColorRepresentation, additive = false) =>
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity,
      depthTest: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4,
      ...(additive ? { blending: THREE.AdditiveBlending } : {}),
    });

  // The circles each mark is draped over, as multiples of the mark's radius.
  // Index: 0 inner ring · 1 the ring · 2 ticks · 3 points · 4 halo, then the
  // underglow's own rings, which are multiples of ITS radius (GLOW_R × the mark's).
  const CHOSEN_RADII = [0.82, 1.0, 1.14, 1.32, 1.5] as const;
  const GLOW_RINGS = [0.3, 0.6, 0.85, 1.0] as const;
  const GLOW_R = 1.3;                               // the glow disc, in mark radii
  const chosenDrape = new Drape([...CHOSEN_RADII, ...GLOW_RINGS.map((g) => g * GLOW_R)], 48);
  const hoverDrape = new Drape([1.0], 48);

  // Pale, thin, broken, and turning — this one is only tracking the pointer.
  // Warm-white rather than grey: it has to read on bare camp earth as well as
  // on grass, and a cool grey ring over a trampled clearing is invisible.
  const hoverRing = new Strip(mat(HOVER_ALPHA, 0xfff4e0), 3, 0.52, 20, 0.07);

  // The circle. Gold and closed: the god has decided about this one.
  const GOLD = 0xf3c46a;
  const chosenRing = new Strip(mat(CHOSEN_ALPHA, GOLD, true), 1, 0, 64, 0.075);
  // …a finer ring inside it, so the mark has two lines like a thing drawn
  const chosenInner = new Strip(mat(CHOSEN_ALPHA * 0.75, GOLD, true), 1, 0, 64, 0.03);
  // …a ring of small marks outside, turning the other way — the runic edge
  const chosenTicks = new Strip(mat(TICKS_ALPHA, GOLD, true), 28, 0.13, 3, 0.06);
  // …four longer marks further out still, the compass of it
  const chosenPoints = new Strip(mat(TICKS_ALPHA * 0.8, GOLD, true), 4, 1.42, 6, 0.045);
  // …a slow breath, so a chosen thing is alive on screen without flashing
  const chosenHalo = new Strip(mat(HALO_ALPHA, GOLD, true), 1, 0, 64, 0.03);
  // …the light on the ground under all of it
  const chosenGlow = new Disc(new THREE.MeshBasicMaterial({
    map: glowTexture(), transparent: true, opacity: GLOW_ALPHA,
    depthTest: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4,
    side: THREE.DoubleSide,
  }), GLOW_RINGS, 32);
  // …and the motes rising off it — depth-tested too, so a mote behind the
  // trunk is behind the trunk
  const moteGeo = new THREE.BufferGeometry();
  const motePos = new Float32Array(MOTES * 3);
  moteGeo.setAttribute('position', new THREE.BufferAttribute(motePos, 3));
  const motes = new THREE.Points(moteGeo, new THREE.PointsMaterial({
    map: moteTexture(), color: GOLD, size: 0.16, sizeAttenuation: true,
    transparent: true, opacity: 0.85, depthTest: true, depthWrite: false,
    blending: THREE.AdditiveBlending, fog: false,
  }));

  // The plain mark — the mortal's chosen ring. One closed line, the hover's
  // ink, ordinary blending: it sits on the turf and does not light it.
  const plainRing = new Strip(mat(PLAIN_ALPHA, 0xfff4e0), 1, 0, 64, 0.08);
  plainRing.mesh.name = 'plainRing';

  hoverRing.mesh.name = 'hoverRing';
  chosenRing.mesh.name = 'chosenRing';
  chosenHalo.mesh.name = 'chosenHalo';
  chosenTicks.mesh.name = 'chosenTicks';
  chosenGlow.mesh.name = 'chosenGlow';
  motes.name = 'chosenMotes';
  const all: THREE.Object3D[] = [hoverRing.mesh, chosenGlow.mesh, chosenHalo.mesh, chosenPoints.mesh,
    chosenTicks.mesh, chosenInner.mesh, chosenRing.mesh, motes, plainRing.mesh];
  for (const r of all) {
    r.frustumCulled = false;
    r.renderOrder = 6;          // over the ground, the paths and the clearings
    r.visible = false;
    group.add(r);
  }

  // hold a legible size at any range, the way the smoke and the trails do
  const radiusFor = (t: Target, eye: { x: number; y: number; z: number }, base: number) => {
    const d = Math.hypot(t.x - eye.x, t.y - eye.y, t.z - eye.z);
    return Math.max(t.size, d * HOLD) * base;
  };
  // the plain mark: a footprint, not a size — and still held legible at range
  const plainRadiusFor = (t: Target, eye: { x: number; y: number; z: number }) => {
    const d = Math.hypot(t.x - eye.x, t.y - eye.y, t.z - eye.z);
    return Math.max(Math.min(Math.max(t.size * 0.5, PLAIN_MIN), PLAIN_MAX), d * HOLD);
  };

  return {
    group,
    update(tSec, hover, chosen, eye, groundAt, plain = false) {
      for (const r of all) r.visible = false;
      if (plain) {
        // the mortal's marks: small, still, and no colour but the hover's ink
        if (hover && (!chosen || chosen.id !== hover.id)) {
          const r = plainRadiusFor(hover, eye);
          hoverDrape.sample(hover.x, hover.z, r, groundAt);
          hoverRing.lay(hoverDrape, 0, r, 0, LIFT_BASE + LIFT_PER_R * r);
        }
        if (chosen) {
          const r = plainRadiusFor(chosen, eye);
          chosenDrape.sample(chosen.x, chosen.z, r, groundAt);
          plainRing.lay(chosenDrape, 1, r, 0, LIFT_BASE + LIFT_PER_R * r);
        }
        return;
      }
      // the pointer's ring gives way to the chosen one on the same thing:
      // two rings on one tree reads as a bug, not as emphasis
      if (hover && (!chosen || chosen.id !== hover.id)) {
        const r = radiusFor(hover, eye, 1);
        hoverDrape.sample(hover.x, hover.z, r, groundAt);
        hoverRing.lay(hoverDrape, 0, r, tSec * HOVER_SPIN, LIFT_BASE + LIFT_PER_R * r);
      }
      if (chosen) {
        // the drawn circle is a little wider than the thing's own footprint —
        // a mark is made AROUND a thing, not on its edge. A small thing gets
        // the full margin; a herd or a landform, whose footprint is already
        // most of a hillside, gets a mark that sits on its edge, or the circle
        // would be the whole frame from anywhere you could have chosen it.
        const B = Math.max(1.0, 1.35 - Math.max(0, chosen.size - 8) * 0.022);
        const r = radiusFor(chosen, eye, B);
        const lift = LIFT_BASE + LIFT_PER_R * r;
        chosenDrape.sample(chosen.x, chosen.z, r, groundAt);
        chosenGlow.lay(chosenDrape, CHOSEN_RADII.length, r * GLOW_R, lift * 0.6, groundAt);
        chosenInner.lay(chosenDrape, 0, r * 0.82, 0, lift);
        chosenRing.lay(chosenDrape, 1, r, 0, lift);
        chosenTicks.lay(chosenDrape, 2, r * 1.14, tSec * CHOSEN_SPIN, lift);
        chosenPoints.lay(chosenDrape, 3, r * 1.32, -tSec * CHOSEN_SPIN * 0.6, lift);
        chosenHalo.lay(chosenDrape, 4, r * (1.5 + 0.06 * Math.sin(tSec * BREATH)), 0, lift);
        // the motes: each on its own slow orbit and its own rise, from a hash
        // of its index — pure in time, so every machine draws the same motes
        for (let i = 0; i < MOTES; i++) {
          const ph = i * 2.399963 + 0.7 * Math.sin(i * 12.9898);
          const cycle = (tSec * (0.11 + 0.03 * (i % 4)) + i * 0.173) % 1;
          const ang = ph + tSec * (0.05 + 0.02 * (i % 3)) * (i % 2 ? 1 : -1);
          const rad = r * (0.7 + 0.5 * ((i * 7) % 5) / 5);
          motePos[i * 3] = chosen.x + Math.cos(ang) * rad;
          motePos[i * 3 + 1] = chosenDrape.heightAt(1, ang) + lift
            + cycle * MOTE_RISE * Math.max(0.5, r * 0.6);
          motePos[i * 3 + 2] = chosen.z + Math.sin(ang) * rad;
        }
        moteGeo.attributes.position.needsUpdate = true;
        (motes.material as THREE.PointsMaterial).size = Math.max(0.12, r * 0.09);
        motes.visible = true;
      }
    },
  };
}
